import Configuration from '../config/Configuration';
import BeaconFilter from '../models/BeaconFilter';
import BeaconFilterItem from '../models/BeaconFilterItem';
import BeaconFilterRange from '../models/BeaconFilterRange';
import BeaconFilterVariant from '../models/BeaconFilterVariant';


function normalizeApiUrl(url: string) {
    return url.endsWith("/") ? url : `${url}/`;
}


const API_ENDPOINT = normalizeApiUrl(Configuration.API_ENDPOINT);


class EnodBeaconClient {
    static async getFilteringTerms(abortSignal: AbortSignal): Promise<BeaconFilterItem[]> {
        const queryParams = { limit: 999 };
        const response = await EnodBeaconClient.get<any>("filtering_terms", queryParams, abortSignal);
        return ((response.response || {}).filteringTerms || []) as BeaconFilterItem[];
    }

    static async countBiosamples(filter: BeaconFilter, abortSignal: AbortSignal): Promise<number> {
        const requestBody = {
            "meta": {
                "beaconId": "org.clinbioinfosspa.enod.ga4ghbeaconv2",
                "apiVersion": "v2.0"
            },
            "query": EnodBeaconClient.translateQuery(filter, "count")
        };
        const response = await EnodBeaconClient.post<any>("biosamples", undefined, requestBody, abortSignal);
        // if ("BeaconCountResponse" !== response.type) {
        if (!("responseSummary" in response)) {
            const error = new Error("Unrecognized response");
            error.name = "500";
            throw error;
        } else {
            return ((response.responseSummary) || {}).numTotalResults || 0;
        }
    }

    static async getVariants(filter: BeaconFilter, abortSignal: AbortSignal): Promise<[number, any[]]> {
        const requestBody = {
            "meta": {
                "beaconId": "org.clinbioinfosspa.enod.ga4ghbeaconv2",
                "apiVersion": "v2.0"
            },
            "query": EnodBeaconClient.translateQuery(filter, "record")
        };
        const response = await EnodBeaconClient.post<any>("g_variants", undefined, requestBody, abortSignal);
        // if ("BeaconResultsetsResponse" !== response.type) {
        if (!("responseSummary" in response && "response" in response)) {
            const error = new Error("Unrecognized response");
            error.name = "500";
            throw error;
        } else {
            const numVariants = (response.responseSummary || {}).numTotalResults || 0;
            const variants = ((response.response) || {}).resultSets[0].results || [];
            const maxNumVariants = !!(((response.response || {}).info || {}).numTotalResults || "");
            return [maxNumVariants ? Infinity : numVariants, variants];
        }
    }

    private static translateQuery(query: BeaconFilter, granularity: string): any {
        const result: any = {
            filters: (query.filters || []) as any[], // .map((elem) => ({ ...elem, ftType: "ontologyTerm" })),
            requestedGranularity: granularity
        };
        if (!!query.variant || !!query.range || !!query.gene) {
            result.requestParameters = {
                g_variant: {
                    assemblyId: "GRCh37"
                }
            };
            if (!!query.variant) {
                result.requestParameters.g_variant = {
                    ...(result.requestParameters.g_variant),
                    referenceName: query.variant.sequence,
                    start: [query.variant.position],
                    alternateBases: query.variant.alternate,
                    referenceBases: query.variant.reference
                };
            }
            if (!!query.range) {
                result.requestParameters.g_variant = {
                    ...(result.requestParameters.g_variant),
                    referenceName: query.range.sequence,
                    start: [query.range.start, query.range.end]
                };
            }
            if (!!query.gene) {
                result.requestParameters.g_variant = {
                    ...(result.requestParameters.g_variant),
                    geneId: query.gene
                };
            }
        }

        return result;
    }

    private static async exchange<T>(serviceUrl: string, httpMethod: string, queryParams: any, requestBody: any, abortSignal: AbortSignal): Promise<T> {
        // const mmpHostUrl = clientConfig.get("mmp-host") as string;

        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        } as any;
        const params = {
            method: httpMethod,
            mode: 'cors',
            headers: headers,
            signal: abortSignal,
            body: undefined === requestBody ? undefined : JSON.stringify(requestBody)
        } as RequestInit;
        const queryStr = undefined === queryParams ? "" : Object.entries(queryParams)
            .filter(([paramName, paramValue]) => undefined !== paramValue)
            .map(([paramName, paramValue]) => {
                const value: string = paramValue instanceof Date ? paramValue.toISOString() : paramValue as string;
                return `${encodeURIComponent(paramName)}=${encodeURIComponent(value)}`;
            }).join("&");
        const queryUrl = `${API_ENDPOINT}${serviceUrl}${!!queryStr ? "?" : ""}${queryStr}`;
        const response = await fetch(queryUrl, params);
        if (!!response.ok) {
            const actualResponse = await response.json();
            return actualResponse;
        } else {
            const error = new Error(response.statusText);
            error.name = `${response.status}`;
            throw error;
        }
    }

    private static async get<T>(serviceUrl: string, queryParams: any, abortSignal: AbortSignal): Promise<T> {
        return EnodBeaconClient.exchange(serviceUrl, "GET", queryParams, undefined, abortSignal);
    }

    private static async put<T>(serviceUrl: string, queryParams: any, requestBody: any, abortSignal: AbortSignal): Promise<T> {
        return EnodBeaconClient.exchange(serviceUrl, "PUT", queryParams, requestBody, abortSignal);
    }

    private static async post<T>(serviceUrl: string, queryParams: any, requestBody: any, abortSignal: AbortSignal): Promise<T> {
        return EnodBeaconClient.exchange(serviceUrl, "POST", queryParams, requestBody, abortSignal);
    }

    private static async delete<T>(serviceUrl: string, queryParams: any, abortSignal: AbortSignal): Promise<T> {
        return EnodBeaconClient.exchange(serviceUrl, "DELETE", queryParams, undefined, abortSignal);
    }
}


export default EnodBeaconClient;
